import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { subscribe, unsubscribeAll } from '../platform/events/lifecycle';
import { emitLiveAgentAnalytics } from './analytics';
import { MessageType } from '../shared/message';
import { logger } from '../shared/utils/logger';
import {
  initializeSprinklrChat,
  destroySprinklrChat,
} from '../platform/transport/sprinklr-chat-service';
import {
  startConversation,
  endConversation,
  getMessages,
  sendMessage,
  sendMessageWithImage,
  onMessageReceived,
} from '../platform/transport/sprinklr-conversation';
import {
  LIVE_AGENT_ANALYTICS_CONNECTION_STRING,
  LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
  LIVE_AGENT_ANALYTICS_CUSTOM_MESSAGE_PREFIX,
  LIVE_AGENT_ANALYTICS_FIRST_MESSAGE_SUFFIX,
} from './constants';

/**
 * The Sprinklr live-agent engine — a NON-HOOK factory (Seq F.c).
 *
 * This is the block contract from "Enforced design pattern — blocks vs. core": a block's
 * data/effect layer is a factory, never a hook, because a hook forces its consumer to
 * instantiate it unconditionally, synchronously, from a static import — which is exactly
 * what makes an optional-per-journey block impossible to gate. React lives in exactly one
 * file in this module (`useLiveAgentBridge`), two levels above this one.
 *
 * WHAT IT IS, IN ONE SENTENCE: a **reconciliation loop**. `isEscalated` is the persisted
 * INTENT that messages should route to Sprinklr; "connected" is the OBSERVED fact that the
 * SDK and a conversation are live. Every way those two drift apart — a page reload, an idle
 * teardown, a conversation a peer tab started, a send against a dead conversation —
 * converges in `ensureConnected`, so no caller ever has to ask whether the block is
 * connected. Naming that loop is what let six bespoke gap-closings collapse into one.
 *
 * The instance owns its own mutable fields (what used to be 19 `useRef`s) and reads
 * everything that changes underneath it through two injected ports, so nothing here is
 * captured at construction time:
 *
 * @param {Object}   ports
 * @param {() => Object} ports.getContext  the live consumer context, read at FIRE time —
 *   `{ enabled, idleTTL, store, sessionId, onAgentMessage, onConversationEnd,
 *      onSurveyReceived, onImageReceived, onIdleDisconnect, setEscalated }`.
 *   One getter replaces the eight keep-a-ref-in-sync effects the hook used to run: those
 *   effects existed only so SDK callbacks and timers could see the latest render's values,
 *   which is precisely what a getter does without any scheduling.
 * @param {Object}   ports.persistence  the `maa-live-agent` record (this block is its sole
 *   WRITER; Shell hooks cross-read it).
 * @param {() => Object} ports.persistence.read   current record — must be the SYNCHRONOUS
 *   authority, not a render-time snapshot, or two writes in one tick both merge onto the
 *   same stale base and the second clobbers the first (the B.2 lost-update hazard).
 * @param {(record: Object) => void} ports.persistence.write  replace the record.
 * @returns {Object} `init` / `dispose` plus the capability surface the façade re-exports.
 */
export const createSprinklrEngine = ({ getContext, persistence }) => {
  const seed = persistence.read() || {};

  // Observed state — "the SDK + conversation are actually live" — as distinct from
  // `isEscalated`, which is the INTENT that messages should route to Sprinklr. The store is
  // its UI home and the flows' fire-time source; this field is the engine's own synchronous
  // read (SDK callbacks and dispose() have no render to read from). It is deliberately NOT
  // part of the returned surface — one home, one reader rule.
  let isAgentConnected = false;
  let conversationId = seed.conversationId || null;
  let caseNumber = seed.caseNumber ?? null;
  let unsubscribeMessages = null;
  let unsubscribeBus = null;
  let surveyTimeout = null;
  let channel = null;
  let hasFirstMessageBeenSent = false;

  // The ids of the conversation that most recently existed, kept after `conversationId` is
  // cleared so disconnect and survey analytics can still name it. Read through
  // `getConversationIds()` — the façade's survey handlers need them LIVE at fire time, since
  // the engine rotates the conversation between renders.
  let lastConversationId = seed.conversationId || null;
  let lastCaseNumber = seed.caseNumber ?? null;
  const getConversationIds = () => ({
    conversationId: lastConversationId,
    caseNumber: lastCaseNumber,
  });

  const setAgentConnected = (value) => {
    isAgentConnected = value;
    const { store } = getContext();
    if (store) store.getState().setIsAgentConnected(value);
  };

  // Escalation is owned by the conversation store slice (the single source of truth); the
  // consumer passes `setEscalated` = the store action.
  const publishEscalation = (value) => {
    const { setEscalated } = getContext();
    if (setEscalated) setEscalated(value);
  };

  // Single write-path for the persisted record. Escalation/activity writes used to spread a
  // render-scoped copy (`setX({ ...record, x })`), so two writes in the same tick both merged
  // onto the SAME stale base and the second clobbered the first — e.g. an activity touch
  // reverting a just-set escalation. `persistence.read()` is the synchronous authority, so
  // consecutive writes in a tick accumulate.
  const writeSession = (patch) => {
    persistence.write({ ...persistence.read(), ...patch });
  };

  const setEscalationActive = () => {
    publishEscalation(true);
    writeSession({
      isEscalated: true,
      conversationId,
      caseNumber,
      lastActivityAt: Date.now(),
    });
  };

  const clearEscalation = () => {
    publishEscalation(false);
    conversationId = null;
    caseNumber = null;
    writeSession({
      isEscalated: false, conversationId: null, caseNumber: null, lastActivityAt: null,
    });
  };

  // ------ Idle TTL check — disconnect if the user has been idle for > the resolved TTL.
  // A function rather than a pre-computed boolean so `Date.now()` is evaluated at call time.
  const checkIdleExpired = () => {
    const record = persistence.read() || {};
    const { idleTTL } = getContext();
    return !!(record.isEscalated
      && record.lastActivityAt
      && (Date.now() - record.lastActivityAt > idleTTL));
  };

  const updateLastActivity = () => {
    const now = Date.now();
    writeSession({ lastActivityAt: now });
    if (channel) {
      channel.postMessage({ type: 'MAA_UPDATE_LAST_ACTIVITY', lastActivityAt: now });
    }
  };

  /**
   * The ONE conversation-end outcome. Every trigger routes here — the agent closing the
   * chat, the user closing it from the drawer header, idle expiry (local or announced by a
   * peer tab), a survey arriving, and a connect that failed twice. It stops delivery,
   * destroys the SDK, emits the disconnect analytics if a connection was live, and clears
   * escalation. It does NOT broadcast to other tabs (it is also called when RECEIVING a
   * broadcast).
   *
   * It deliberately does NOT write the "ended" system message. WHOEVER DETECTS THE END
   * WRITES THE MESSAGE — the SDK close handler, the idle handler, a peer tab (whose write
   * reaches this tab through IndexedDB + broadCastMessageUpdate), or the flow that asked to
   * escalate and got a rejection. That rule replaces the old `skipMessage` flag, which was
   * `true` at five of six call sites and only encoded "someone else already wrote it".
   *
   * @param {object}  [opts]
   * @param {boolean} [opts.awaitSurvey]  keep the SDK + subscription alive so a survey
   *   MESSAGE_RECEIVED can still arrive (Sprinklr sends it after the close event, with no
   *   deterministic hand-off available yet — see the 30s fallback at the call site).
   */
  const endSession = ({ awaitSurvey = false } = {}) => {
    logger.log('[Sprinklr] endSession called');
    if (surveyTimeout) {
      clearTimeout(surveyTimeout);
      surveyTimeout = null;
    }
    if (!awaitSurvey) {
      if (unsubscribeMessages) {
        unsubscribeMessages();
        unsubscribeMessages = null;
      }
      destroySprinklrChat();
    }
    // Read BEFORE clearing — setAgentConnected resets the flag itself.
    const wasConnected = isAgentConnected;
    setAgentConnected(false);
    if (wasConnected) {
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId: lastConversationId,
        caseNumber: lastCaseNumber,
      });
    }
    clearEscalation();
  };

  /**
   * Event-bus handler for `magic-apron-assistant.close-agent-conversation`
   * (the user chose to end the chat).
   *
   * Only closes the conversation via the SDK — teardown, the "ended" system message, and the
   * post-chat survey are all driven by the CONVERSATIONS_UPDATED subscription event (see
   * `onConversationEnd` below). This keeps user- and agent-initiated closes on a single code
   * path so the user who ends the chat receives the same post-chat survey.
   *
   * A wrapper (rather than binding `endConversation` to the bus directly) is required so it
   * closes the CURRENT conversation instead of the event payload the bus passes to listeners.
   */
  const closeAgentConversation = () => {
    endConversation(conversationId);
  };

  /**
   * Initialises the Sprinklr SDK, starts a conversation, and subscribes to incoming
   * agent messages.
   */
  const initiateAgentChat = async (initialMessage = { text: '' }, agentSeedMessages) => {
    // Inert for journeys that did not declare the live-agent block. Guarded here rather than
    // only in init() because the flows can call this directly.
    if (!getContext().enabled) return;

    // ONE connection attempt. It THROWS on failure: the retry policy lives below rather than
    // in a `[sprinklrError]` effect, because an effect has no caller to reject to — which is
    // why a failed escalation could never reach the flow that asked for it.
    const attempt = async () => {
      const { text } = initialMessage || {};

      if (unsubscribeMessages) {
        unsubscribeMessages();
        unsubscribeMessages = null;
      }

      logger.log('[Sprinklr] initiateAgentChat — starting SDK init…');

      // 1. Initialise the SDK (loads script, waits for READY)
      await initializeSprinklrChat();

      // 2. Start a conversation if there is no existing one (WebSocket is now open)
      if (!conversationId) {
        const initialMessages = [
          text.length > 0 ? { message: text, isSentByUser: true } : '',
          ...(agentSeedMessages || []).map(
            (msg) => ({
              message: typeof (msg.content) === 'string' ? msg.content : msg.content?.responseSummary?.summaryAnswer,
              isSentByUser: msg.type === 'user'
            })
          )
        ];

        try {
          // MAG4-856: Sprinklr gets the ASSISTANT's own session id (the adapter used to read
          // localStorage itself and picked up the wrong one), plus the surface the escalation
          // came from. Only `sessionId` is handed over, not the whole record — the adapter
          // reads nothing else, and `maa-session` has exactly one owner (Seq B.3).
          const {
            conversationId: newConversationId,
            caseNumber: newCaseNumber,
          } = await startConversation(
            { initialMessages },
            { sessionId: getContext().sessionId },
            getContext().deviceType,
          );

          logger.log('[Sprinklr] Conversation started with ID:', newConversationId, 'caseNumber:', newCaseNumber);
          conversationId = newConversationId;
          caseNumber = newCaseNumber;
          lastConversationId = newConversationId;
          lastCaseNumber = newCaseNumber;
          writeSession({ conversationId: newConversationId, caseNumber: newCaseNumber });
          if (channel) {
            channel.postMessage({
              type: 'MAA_ESCALATE_TO_AGENT',
              conversationId: newConversationId,
              caseNumber: newCaseNumber,
            });
          }
        } catch (err) {
          logger.error('[sprinklrEngine] Failed to start conversation:', err);
          throw err;
        }
      }

      // 3. Subscribe to incoming messages — read the handlers through getContext() so the
      //    subscription always calls the consumer's LATEST versions.
      unsubscribeMessages = onMessageReceived({
        onNewMessage: async (incomingMessage) => {
          logger.log('[Sprinklr] Received message from SDK:', incomingMessage);
          const { onAgentMessage } = getContext();
          if (incomingMessage.type === MessageType.AGENT && onAgentMessage) {
            return onAgentMessage(incomingMessage).catch(() => {});
          }
          return Promise.resolve();
        },
        onConversationEnd: () => {
          // Guard against re-entry: closing the conversation can surface more than one
          // CONVERSATIONS_UPDATED event. Once a close is being handled the survey timer is
          // armed, so ignore any subsequent close events.
          if (surveyTimeout) {
            return;
          }

          // Save the "ended" system message for both user- and agent-initiated closes.
          const { onConversationEnd } = getContext();
          if (onConversationEnd) {
            onConversationEnd({ messageId: conversationId });
          }
          // Tell peer tabs: the ended MESSAGE reaches them, escalation STATE does not.
          // The tab that DETECTS announces; endSession never re-broadcasts.
          // See .claude/context/live-agent.md
          if (channel) {
            channel.postMessage({ type: 'MAA_AGENT_DISCONNECTED' });
          }
          // Keep the subscription and SDK instance alive so the survey MESSAGE_RECEIVED
          // event (sent after CONVERSATIONS_UPDATED) can still arrive — regardless of who
          // ended the conversation. A 30-second fallback ensures full cleanup even when
          // Sprinklr sends no survey. ⚠ WORKAROUND, not a requirement: Sprinklr are building
          // a deterministic hand-off via endConversation's return value. Keep it isolated so
          // it can be deleted in one move.
          endSession({ awaitSurvey: true });
          surveyTimeout = setTimeout(() => {
            surveyTimeout = null;
            logger.log('[Sprinklr] No survey received within 30s — performing deferred cleanup');
            endSession();
          }, 30000);
        },
        onSurveyReceived: (surveyUrl) => {
          // Full cleanup — unsubscribes and destroys the SDK (deferred from the close above).
          // endSession also cancels the 30s fallback timer.
          endSession();
          const { onSurveyReceived } = getContext();
          if (onSurveyReceived) {
            onSurveyReceived(surveyUrl);
          }
        },
        onImageReceived: (imageData) => {
          const { onImageReceived } = getContext();
          if (onImageReceived) {
            onImageReceived(imageData);
          }
        },
      });

      setAgentConnected(true);
      hasFirstMessageBeenSent = false;
      setEscalationActive();
      // The one place that sees EVERY route to a live agent — the escalation flow misses
      // peer-tab escalation, reconnect-on-reload, and the image re-escalation. The promotion
      // is idempotent. See .claude/context/live-agent.md
      const { onAgentConnected } = getContext();
      if (onAgentConnected) {
        onAgentConnected();
      }
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_CONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId,
        caseNumber,
      });

      logger.log('[Sprinklr] initiateAgentChat — complete. isAgentConnected=true');
    };

    try {
      await attempt();
    } catch (firstErr) {
      logger.error('[sprinklrEngine] Agent chat init failed; retrying once, clean:', firstErr);
      // The likeliest cause is a stale conversationId (or another stale value), and there is
      // no signal today separating a recoverable conversation from a dead one — so clear and
      // start a NEW conversation rather than retry against the same id, which would just fail
      // again. The message rides along in startConversation's initialMessages, so the retry
      // costs one round trip and emits no second custom-message analytics event.
      setAgentConnected(false);
      clearEscalation();
      try {
        await attempt();
      } catch (secondErr) {
        // Both attempts failed. Clean up the half-initialised SDK and the connecting state,
        // then REJECT so the flow that asked for escalation shows the user an error message.
        // No "conversation ended" system message: nothing ended — the connection never
        // established — and the caller writes the accurate error message instead.
        endSession();
        throw secondErr;
      }
    }
  };

  /**
   * Reconcile intent with reality. `isEscalated` says messages SHOULD route to Sprinklr;
   * this checks whether they actually CAN and closes the gap. Every way the two drift apart
   * — a page reload, an idle teardown, a conversation a peer tab started — resolves here,
   * so callers never have to ask whether the block is connected. That question used to live
   * in flows/askUserQuestion, which is exactly the coupling this removes.
   */
  const ensureConnected = async () => {
    if (!getContext().enabled || isAgentConnected) return;
    await initiateAgentChat();
  };

  /**
   * Sends a user message through the Sprinklr conversation channel.
   */
  const sendAgentMessage = async (text, actionId) => {
    logger.log('[Sprinklr] sendAgentMessage:', text);
    const isFirstLCMessage = !hasFirstMessageBeenSent;
    hasFirstMessageBeenSent = true;
    const suffix = isFirstLCMessage ? LIVE_AGENT_ANALYTICS_FIRST_MESSAGE_SUFFIX : '';
    emitLiveAgentAnalytics({
      lvQuestion: `${LIVE_AGENT_ANALYTICS_CUSTOM_MESSAGE_PREFIX}${text}${suffix}`,
      sessionId: getContext().sessionId,
      actionId,
      conversationId,
      caseNumber,
    });
    await ensureConnected();
    try {
      await sendMessage(text, conversationId, actionId);
    } catch (err) {
      logger.error('[sprinklrEngine] Failed to send message:', err);
      // Recovery, per the product rule "get them back to a human however possible": the
      // likeliest cause is a stale conversation, so start clean and deliver this message as
      // the new conversation's opening message. If THAT fails the rejection propagates —
      // the caller owns telling the user, which is why both call sites have a catch.
      clearEscalation();
      await initiateAgentChat({ text, actionId });
    }
  };

  /**
   * Sends a user image message through the Sprinklr conversation channel.
   */
  const sendAgentImage = async (text, file, actionId) => {
    logger.log('[Sprinklr] sendAgentImage:', file?.name);
    await ensureConnected();
    try {
      await sendMessageWithImage(text, file, conversationId, actionId);
    } catch (err) {
      logger.error('[sprinklrEngine] Failed to send agent image:', err);
      // Same recovery as sendAgentMessage. It also fixes a latent bug: the old
      // `[sprinklrError]` effect resent `messagePayload` — the last TEXT message — after an
      // image failure, so the image itself was never retried (and the resend threw a
      // TypeError outright when no text had been sent yet). Reconnect, resend the IMAGE.
      clearEscalation();
      await initiateAgentChat();
      await sendMessageWithImage(text, file, conversationId, actionId);
    }
  };

  /**
   * Handles idle TTL expiry — tears down the SDK, clears escalation, and broadcasts to other
   * tabs. Does NOT save any message; the consumer's `onIdleDisconnect` does that, because the
   * idle wording is deliberately distinct from every other end.
   */
  const handleIdleDisconnect = async () => {
    await endSession();
    if (channel) {
      channel.postMessage({ type: 'MAA_IDLE_DISCONNECT' });
    }
  };

  /**
   * Cross-tab escalation sync. The conversation is intentionally live in EVERY tab so the
   * user can move between them mid-chat.
   */
  const handleBroadcast = ({
    data: {
      type,
      conversationId: newConversationId,
      caseNumber: newCaseNumber,
      lastActivityAt,
    }
  }) => {
    if (type === 'MAA_ESCALATE_TO_AGENT') {
      setEscalationActive();
      if (newConversationId) {
        conversationId = newConversationId;
        caseNumber = newCaseNumber ?? null;
        lastConversationId = newConversationId;
        lastCaseNumber = newCaseNumber ?? null;
        writeSession({ conversationId: newConversationId, caseNumber: newCaseNumber });
      }
      initiateAgentChat().catch(() => {});
    }
    if (type === 'MAA_IDLE_DISCONNECT') {
      endSession();
    }
    // A peer ended the conversation. No message is written here — whoever DETECTED it did.
    if (type === 'MAA_AGENT_DISCONNECTED') {
      endSession({ awaitSurvey: true });
      surveyTimeout = setTimeout(() => {
        surveyTimeout = null;
        logger.log('[Sprinklr] No survey received within 30s — performing deferred cleanup');
        endSession();
      }, 30000);
    }
    if (type === 'MAA_UPDATE_LAST_ACTIVITY' && lastActivityAt) {
      writeSession({ lastActivityAt });
    }
  };

  /**
   * Reconnect-on-reload: a persisted escalation with a conversation id means the user was
   * mid-chat when the page went away. This is a REQUIREMENT, not an optimisation.
   */
  const resumeConversation = async () => {
    await initiateAgentChat().catch(() => {});

    const recentMessageResponse = await getMessages(
      { conversationId, limit: 10 }
    ).catch(() => ({ messages: [], closed: true }));
    const { messages: recentMessages, closed } = recentMessageResponse;
    if (closed) {
      // Conversation is closed — this tab DETECTED the end, so this tab writes the ended
      // message (the rule endSession documents), then cleans up. `messageId` is null here
      // because clearEscalation() has already run, which matches the previous behaviour:
      // teardown read the same value after the same clear.
      clearEscalation();
      const { onConversationEnd } = getContext();
      if (onConversationEnd) {
        onConversationEnd({ messageId: conversationId });
      }
      endSession();
      return;
    }

    recentMessages.forEach((msg) => {
      const { onAgentMessage } = getContext();
      if (onAgentMessage) {
        onAgentMessage(msg).catch(() => {});
      }
    });
  };

  /**
   * Start the block: hydrate intent, open the cross-tab channel, subscribe to the host
   * lifecycle bus, then reconcile (reconnect-on-reload / idle expiry).
   *
   * These five steps were five `useEffect`s. They are ordered exactly as React ran them
   * (source order), and each one now runs ONCE — where before, two of them re-ran whenever a
   * callback identity changed, which made the engine's effect timing a function of its
   * re-render cadence.
   */
  const init = () => {
    const { enabled } = getContext();
    // Journeys without live agent never hydrate escalation — the store stays false, and
    // every step below is skipped.
    if (!enabled) return;

    // Escalation lives in the store; `maa-live-agent` is its persisted projection. Hydrate
    // once so store.isEscalated reflects the durable value; transitions write through
    // publishEscalation thereafter.
    publishEscalation(persistence.read()?.isEscalated ?? false);

    if (typeof BroadcastChannel !== 'undefined') {
      channel = createBroadcastChannel(CHANNELS.assistant);
      channel.onmessage = handleBroadcast;
    }

    // Defensively clear any handler left by a prior instance before registering this one.
    unsubscribeAll('magic-apron-assistant.close-agent-conversation');
    unsubscribeBus = subscribe('magic-apron-assistant.close-agent-conversation', closeAgentConversation);

    const record = persistence.read() || {};
    if (record.isEscalated && record.conversationId) {
      resumeConversation();
    }

    if (checkIdleExpired()) {
      handleIdleDisconnect().catch(() => {});
      const { onIdleDisconnect } = getContext();
      if (onIdleDisconnect) {
        onIdleDisconnect();
      }
    }
  };

  /**
   * Stop the block. Mirrors init()'s teardown in the order React ran the cleanups. It does
   * NOT destroy the SDK: the drawer closing does not end a conversation, and the only
   * unmount in production is the page going away.
   */
  const dispose = () => {
    if (channel) {
      channel.close();
      channel = null;
    }
    if (unsubscribeBus) {
      unsubscribeBus();
      unsubscribeBus = null;
    }
    if (unsubscribeMessages) {
      unsubscribeMessages();
      unsubscribeMessages = null;
    }
    // ⚠ Reads the instance field, not React state. As a `[]`-effect cleanup this closed over
    // the FIRST render, where the flag is always `false`, so the disconnect event could never
    // fire on unmount (navigation/reload while connected) — a real bug fixed in `190f013`.
    if (isAgentConnected) {
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId: lastConversationId,
        caseNumber: lastCaseNumber,
      });
    }
  };

  return {
    init,
    dispose,
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    sendAgentImage,
    clearEscalation,
    updateLastActivity,
    handleIdleDisconnect,
    getConversationIds,
  };
};
